import { formatUnits } from 'viem'

import type { InterfaceToken } from '@morpho-org/dapp-core'

import type { WalletType } from '@/lib/observability/shared/wallet-type'

import type { MarketBorrowEntryPoint } from '../events'
import { trackAnalyticsEvent } from '../track'

export interface MarketBorrowCompletedParams {
  marketId: string
  loanAssetSymbol: string
  loanAssetAddress: string
  collateralAssetSymbol: string
  collateralAssetAddress: string
  borrowAmountRaw: string
  borrowAmountUsd: number
  collateralAmountRaw: string | null
  collateralAmountUsd: number | null
  chainId: number
  chainName: string
  walletType: WalletType
  entryPoint: MarketBorrowEntryPoint
}

export function trackMarketBorrowCompleted(params: MarketBorrowCompletedParams) {
  trackAnalyticsEvent('market_borrow_completed', {
    market_id: params.marketId,
    loan_asset_symbol: params.loanAssetSymbol,
    loan_asset_address: params.loanAssetAddress,
    collateral_asset_symbol: params.collateralAssetSymbol,
    collateral_asset_address: params.collateralAssetAddress,
    borrow_amount_raw: params.borrowAmountRaw,
    borrow_amount_usd: params.borrowAmountUsd,
    collateral_amount_raw: params.collateralAmountRaw,
    collateral_amount_usd: params.collateralAmountUsd,
    chain_id: params.chainId,
    chain_name: params.chainName,
    wallet_type: params.walletType,
    entry_point: params.entryPoint
  })
}

export interface BuildMarketBorrowCompletedArgs {
  marketId: string
  loanAsset: InterfaceToken | null | undefined
  collateralAsset: InterfaceToken | null | undefined
  loanAmount: bigint
  collateralAmount: bigint
  chainId: number
  chainName: string
  walletType: WalletType
  entryPoint: MarketBorrowEntryPoint
}

/**
 * Assemble the `market_borrow_completed` payload from raw inputs, or `null` when there is
 * nothing to report (no borrow, or token symbols not yet resolved). Shared by the legacy and
 * marketActionsV2 finalize handlers so the toUsd / collateral null-guard logic lives in one place.
 */
export function buildMarketBorrowCompletedParams({
  marketId,
  loanAsset,
  collateralAsset,
  loanAmount,
  collateralAmount,
  chainId,
  chainName,
  walletType,
  entryPoint
}: BuildMarketBorrowCompletedArgs): MarketBorrowCompletedParams | null {
  if (loanAmount <= 0n || !loanAsset?.symbol || !collateralAsset?.symbol) return null

  const hasCollateral = collateralAmount > 0n
  return {
    marketId,
    loanAssetSymbol: loanAsset.symbol,
    loanAssetAddress: loanAsset.address,
    collateralAssetSymbol: collateralAsset.symbol,
    collateralAssetAddress: collateralAsset.address,
    borrowAmountRaw: loanAmount.toString(),
    borrowAmountUsd: Number(formatUnits(loanAsset.toUsd(loanAmount) ?? 0n, 18)),
    collateralAmountRaw: hasCollateral ? collateralAmount.toString() : null,
    collateralAmountUsd: hasCollateral
      ? Number(formatUnits(collateralAsset.toUsd(collateralAmount) ?? 0n, 18))
      : null,
    chainId,
    chainName,
    walletType,
    entryPoint
  }
}
